import { Link } from "react-router-dom";
import { Star } from "lucide-react";
import { useBrands } from "../api/hooks";
import { BrandLogo } from "../components/OfferCard";
import { GridSkeleton, ErrorState, EmptyState } from "../components/States";
import { MaskedLine, Reveal } from "../components/Motion";
import { Seo } from "../components/Seo";

const BrandsPage = () => {
    const { data: brands, isLoading, isError, refetch } = useBrands();

    return (
        <>
            <Seo
                title="Brands — SahiKhoj"
                description="Browse offers and coupons from supported brands and merchants on SahiKhoj."
                path="/brands"
            />
            <section className="hero-glow bg-ink pb-14 pt-36 text-cream sm:pb-16 lg:pt-44">
                <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
                    <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.24em] text-mist">
                        <span className="h-px w-10 bg-saffron" />
                        Stores
                    </p>
                    <h1 className="mt-6 font-display text-4xl font-semibold leading-[1.06] tracking-tighter sm:text-5xl">
                        <MaskedLine delay={0.15}>
                            Brands on <span className="text-saffron">SahiKhoj.</span>
                        </MaskedLine>
                    </h1>
                    {brands && (
                        <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.18em] text-mist/70" data-testid="brands-count">
                            {brands.length} brand{brands.length === 1 ? "" : "s"}
                        </p>
                    )}
                </div>
            </section>

            <section className="bg-cream py-14 sm:py-20" data-testid="brands-page">
                <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
                    {isLoading && <GridSkeleton count={10} />}
                    {isError && <ErrorState onRetry={refetch} />}
                    {brands && brands.length === 0 && (
                        <EmptyState title="No brands yet" message="Brands will appear here as offers are added." />
                    )}
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
                        {(brands || []).map((b, i) => (
                            <Reveal key={b.id} delay={Math.min(i * 0.03, 0.4)}>
                                <Link
                                    to={`/brands/${b.slug}`}
                                    data-testid={`brand-card-${b.slug}`}
                                    className="group relative flex h-full flex-col items-center gap-3 rounded-2xl border border-ink/5 bg-white p-6 text-center shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                                >
                                    {b.isFeatured && (
                                        <Star className="absolute right-3 top-3 h-3.5 w-3.5 fill-saffron text-saffron" aria-label="Featured brand" />
                                    )}
                                    <BrandLogo brand={b} className="h-12 w-12" />
                                    <span className="line-clamp-1 text-sm font-medium text-ink">{b.name}</span>
                                    {b.offerCount != null && (
                                        <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-smoke">
                                            {b.offerCount} offer{b.offerCount === 1 ? "" : "s"}
                                        </span>
                                    )}
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
};

export default BrandsPage;
